import React from 'react';
import { Loader2, Radio, Clock, Eye, X, Copy, Play, StopCircle, Trash2, FlaskConical } from 'lucide-react';

export default function LiveStreamingTab({
  liveStreams, showCreateStream, setShowCreateStream, streamForm, setStreamForm,
  streamLoading, selectedStream, setSelectedStream, handleCreateStream,
  handleGoLive, handleEndStream, handleDeleteStream, copyToClipboard
}) {
  return (
    <div data-testid="live-streaming-section">
      {/* Create Stream Modal */}
      {showCreateStream && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#2a2a2a] rounded-xl p-6 max-w-lg w-full">
            <h2 className="text-xl font-bold text-white mb-4">Create New Live Stream</h2>
            <form onSubmit={handleCreateStream} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Stream Title*</label>
                <input
                  type="text"
                  required
                  value={streamForm.title}
                  onChange={(e) => setStreamForm({ ...streamForm, title: e.target.value })}
                  placeholder="e.g., AFCON 2025 - Nigeria vs Cameroon"
                  className="w-full px-3 py-2 bg-[#1a1a1a] border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-red-500"
                  data-testid="stream-title-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Teams</label>
                <input
                  type="text"
                  value={streamForm.teams}
                  onChange={(e) => setStreamForm({ ...streamForm, teams: e.target.value })}
                  placeholder="e.g., Nigeria vs Cameroon"
                  className="w-full px-3 py-2 bg-[#1a1a1a] border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-red-500"
                  data-testid="stream-teams-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Competition</label>
                <input
                  type="text"
                  value={streamForm.competition_name}
                  onChange={(e) => setStreamForm({ ...streamForm, competition_name: e.target.value })}
                  placeholder="e.g., AFCON 2025"
                  className="w-full px-3 py-2 bg-[#1a1a1a] border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-red-500"
                  data-testid="stream-competition-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Scheduled Start Time
                  <span className="ml-2 text-xs text-amber-400 font-normal">(Optional - auto-publish when stream starts)</span>
                </label>
                <input
                  type="datetime-local"
                  value={streamForm.scheduled_start || ''}
                  onChange={(e) => setStreamForm({ ...streamForm, scheduled_start: e.target.value })}
                  className="w-full px-3 py-2 bg-[#1a1a1a] border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-red-500"
                  data-testid="stream-schedule-input"
                />
                <p className="text-xs text-gray-500 mt-1">If set, stream will be auto-published when you go live</p>
              </div>
              <label className="flex items-center gap-2 text-gray-300">
                <input
                  type="checkbox"
                  checked={streamForm.is_premium}
                  onChange={(e) => setStreamForm({ ...streamForm, is_premium: e.target.checked })}
                  className="rounded border-gray-600 bg-[#1a1a1a] text-red-600 focus:ring-red-500"
                  data-testid="stream-premium-checkbox"
                />
                Premium content (subscribers only)
              </label>
              <div
                data-testid="test-stream-toggle"
                onClick={() => setStreamForm({ ...streamForm, is_test: !streamForm.is_test })}
                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                  streamForm.is_test
                    ? 'bg-amber-500/10 border-amber-500/40'
                    : 'bg-[#1a1a1a] border-gray-600 hover:border-gray-500'
                }`}
              >
                <FlaskConical className={`w-5 h-5 ${streamForm.is_test ? 'text-amber-400' : 'text-gray-500'}`} />
                <div className="flex-1">
                  <p className={`text-sm font-semibold ${streamForm.is_test ? 'text-amber-300' : 'text-gray-300'}`}>
                    Test Stream Mode
                  </p>
                  <p className="text-xs text-gray-500">
                    Labels as "Test Broadcast" · Hidden from users · Excluded from analytics
                  </p>
                </div>
                <div className={`w-9 h-5 rounded-full transition-colors relative ${streamForm.is_test ? 'bg-amber-500' : 'bg-gray-600'}`}>
                  <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${streamForm.is_test ? 'translate-x-4' : 'translate-x-0.5'}`} />
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCreateStream(false)}
                  className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg"
                  data-testid="stream-cancel-btn"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={streamLoading}
                  className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-lg flex items-center justify-center gap-2"
                  data-testid="stream-create-btn"
                >
                  {streamLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Radio className="w-4 h-4" />}
                  {streamForm.scheduled_start ? 'Schedule Stream' : 'Create Stream'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* RTMP Credentials Panel */}
      {selectedStream && (
        <div className="bg-gradient-to-r from-red-900/30 to-orange-900/30 border border-red-500/30 rounded-xl p-6 mb-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h3 className="text-lg font-bold text-white">Stream Credentials</h3>
              <p className="text-gray-400 text-sm">Use these in OBS or your broadcasting software</p>
            </div>
            <button onClick={() => setSelectedStream(null)} className="text-gray-400 hover:text-white" data-testid="close-credentials-btn">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">RTMP URL</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  readOnly
                  value={selectedStream.rtmps_url}
                  className="flex-1 px-3 py-2 bg-black/40 border border-gray-700 rounded-lg text-white font-mono text-sm"
                />
                <button
                  onClick={() => copyToClipboard(selectedStream.rtmps_url, 'RTMP URL')}
                  className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg"
                  data-testid="copy-rtmp-url"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">Stream Key (keep secret!)</label>
              <div className="flex gap-2">
                <input
                  type="password"
                  readOnly
                  value={selectedStream.rtmps_stream_key}
                  className="flex-1 px-3 py-2 bg-black/40 border border-gray-700 rounded-lg text-white font-mono text-sm"
                />
                <button
                  onClick={() => copyToClipboard(selectedStream.rtmps_stream_key, 'Stream Key')}
                  className="px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg"
                  data-testid="copy-stream-key"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <div className="bg-amber-900/20 border border-amber-500/30 rounded-lg p-3 text-amber-300 text-sm">
              <strong>Next steps:</strong> Start streaming from OBS, then click "Go Live" below to publish.
            </div>
          </div>
        </div>
      )}

      {/* Live Streams Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {liveStreams.length === 0 ? (
          <div className="col-span-full text-center py-16 bg-[#222] rounded-xl" data-testid="empty-streams">
            <Radio className="w-12 h-12 text-gray-500 mx-auto mb-4" />
            <p className="text-gray-400">No live streams yet</p>
            <p className="text-gray-500 text-sm mt-1">Create a stream to get started</p>
          </div>
        ) : (
          liveStreams.map((stream) => (
            <div 
              key={stream.stream_id} 
              className={`bg-[#222] rounded-xl p-4 border ${stream.is_live ? 'border-red-500' : 'border-gray-700'}`}
              data-testid={`stream-card-${stream.stream_id}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-bold text-white truncate">{stream.title}</h3>
                  <p className="text-gray-400 text-sm">{stream.teams || 'No teams specified'}</p>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {stream.is_test && (
                    <span className="flex items-center gap-1 px-2 py-1 bg-amber-500/15 text-amber-400 text-xs font-bold rounded border border-amber-500/30" data-testid={`stream-test-badge-${stream.stream_id}`}>
                      <FlaskConical className="w-3 h-3" />
                      TEST
                    </span>
                  )}
                  {stream.is_live ? (
                  <span className="flex items-center gap-1 px-2 py-1 bg-red-600 text-white text-xs font-bold rounded">
                    <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                    LIVE
                  </span>
                ) : stream.status === 'scheduled' ? (
                  <span className="px-2 py-1 bg-amber-600/20 text-amber-400 text-xs rounded border border-amber-500/30">
                    Scheduled
                  </span>
                ) : (
                  <span className="px-2 py-1 bg-gray-700 text-gray-300 text-xs rounded">
                    {stream.status === 'ended' ? 'Ended' : 'Idle'}
                  </span>
                )}
                </div>
              </div>
              
              <div className="text-xs text-gray-500 mb-4">
                {stream.competition_name && <span className="text-emerald-400">{stream.competition_name}</span>}
                {stream.is_premium && <span className="ml-2 text-amber-400">Premium</span>}
                {stream.scheduled_start && (
                  <div className="mt-1 text-amber-300">
                    <Clock className="w-3 h-3 inline mr-1" />
                    {new Date(stream.scheduled_start).toLocaleString()}
                  </div>
                )}
              </div>
              
              <div className="flex gap-2">
                <button
                  onClick={() => setSelectedStream(stream)}
                  className="flex-1 px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-lg flex items-center justify-center gap-1"
                  data-testid={`stream-credentials-${stream.stream_id}`}
                >
                  <Eye className="w-3 h-3" />
                  Credentials
                </button>
                
                {stream.is_live ? (
                  <button
                    onClick={() => handleEndStream(stream.stream_id)}
                    className="flex-1 px-3 py-1.5 bg-orange-600 hover:bg-orange-700 text-white text-sm rounded-lg flex items-center justify-center gap-1"
                    data-testid={`stream-end-${stream.stream_id}`}
                  >
                    <StopCircle className="w-3 h-3" />
                    End
                  </button>
                ) : stream.status !== 'ended' && (
                  <button
                    onClick={() => handleGoLive(stream.stream_id)}
                    className="flex-1 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg flex items-center justify-center gap-1"
                    data-testid={`stream-golive-${stream.stream_id}`}
                  >
                    <Play className="w-3 h-3" />
                    Go Live
                  </button>
                )}
                
                <button
                  onClick={() => handleDeleteStream(stream.stream_id)}
                  className="px-3 py-1.5 bg-gray-800 hover:bg-red-900/50 text-gray-400 hover:text-red-400 text-sm rounded-lg"
                  data-testid={`stream-delete-${stream.stream_id}`}
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
